import React, { useState, useEffect } from 'react';
import {
  getWorkshopInterestSurvey,
  emptySurveyValues,
  pruneHiddenSurveyValues,
  validateInterestSurvey,
  interestSurveyToPayload,
  isFieldVisible,
  InterestSurveyValues,
  SurveyField,
} from '../../data/workshop2026InterestSurvey';
import { useLocale } from '../../context/LocaleContext';
import styles from '../../styles/workshopInterestModal.module.css';

interface WorkshopInterestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: ReturnType<typeof interestSurveyToPayload>) => Promise<void>;
}

const WorkshopInterestModal: React.FC<WorkshopInterestModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { t, locale } = useLocale();
  const [values, setValues] = useState<InterestSurveyValues>(emptySurveyValues);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const survey = getWorkshopInterestSurvey();

  useEffect(() => {
    if (!isOpen) {
      setValues(emptySurveyValues());
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const setFieldValue = (fieldId: string, value: string) => {
    setValues((prev) => pruneHiddenSurveyValues({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateInterestSurvey(values);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(interestSurveyToPayload(values));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('workshop.2026.interest_modal.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field: SurveyField) => {
    if (!isFieldVisible(field, values)) return null;

    if (field.type === 'select') {
      return (
        <div key={`${locale}-${field.id}`} className={styles.field}>
          <label className={styles.fieldLabel} htmlFor={field.id}>
            {field.label}
          </label>
          <select
            id={field.id}
            className={styles.select}
            value={values[field.id] || ''}
            onChange={(e) => setFieldValue(field.id, e.target.value)}
            required
          >
            <option value="">{t('workshop.2026.interest_modal.select')}</option>
            {field.options.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          {field.helper && <p className={styles.helper}>{field.helper}</p>}
        </div>
      );
    }

    return (
      <div key={`${locale}-${field.id}`} className={styles.field}>
        <span className={styles.fieldLabel}>{field.label}</span>
        <div className={styles.radioGroup}>
          {field.options.map(({ value, label }) => (
            <label key={value} className={styles.radioLabel}>
              <input
                type="radio"
                name={field.id}
                value={value}
                checked={values[field.id] === value}
                onChange={() => setFieldValue(field.id, value)}
              />
              {label}
            </label>
          ))}
        </div>
        {field.helper && <p className={styles.helper}>{field.helper}</p>}
      </div>
    );
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={styles.header}>
          <div>
            <h2>{t('workshop.2026.interest_modal.title')}</h2>
            <p className={styles.subtitle}>{t('workshop.2026.interest_modal.subtitle')}</p>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label={t('workshop.2026.interest_modal.close')}
          >
            ×
          </button>
        </div>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.body}>
            {survey.map(renderField)}
            {error && <p className={styles.error}>{error}</p>}
          </div>

          <div className={styles.footer}>
            <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={submitting}>
              {t('workshop.2026.interest_modal.cancel')}
            </button>
            <button type="submit" className={styles.submitBtn} disabled={submitting}>
              {submitting
                ? t('workshop.2026.interest_modal.submitting')
                : t('workshop.2026.interest_modal.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WorkshopInterestModal;
